import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, SkipForward, X, MoreVertical } from 'lucide-react';
import { useSound } from '../hooks/useSound';

/**
 * Floating gameplay menu - bottom right.
 * Replaces the standalone MuteButton on /play/:game.
 * Tap the pill to expand 3 actions: Volume toggle, Skip game, End game.
 */
export function GameMenu({ onSkip, onExit }) {
  const { muted, toggleMute } = useSound();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.55rem 0.85rem',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    borderRadius: '0.5rem',
  };

  return (
    <div
      ref={wrapRef}
      data-testid="game-menu"
      style={{
        position: 'fixed',
        bottom: '5rem',
        right: '1.5rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '0.85rem',
      }}
    >
      {open && (
        <div
          data-testid="game-menu-panel"
          style={{
            background: 'var(--surface)',
            border: '2px solid var(--border)',
            borderRadius: '0.75rem',
            boxShadow: '0 6px 20px var(--shadow)',
            padding: '0.35rem',
            minWidth: 180,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.15rem',
            animation: 'fade-in-scale 0.15s ease',
            transformOrigin: 'bottom right',
          }}
        >
          <button
            data-testid="game-menu-mute"
            style={itemStyle}
            onClick={() => { toggleMute(); setOpen(false); }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-el)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            <span>{muted ? 'Unmute' : 'Mute volume'}</span>
          </button>

          <button
            data-testid="game-menu-skip"
            style={itemStyle}
            onClick={() => { setOpen(false); onSkip?.(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-el)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <SkipForward size={18} />
            <span>Skip to end</span>
          </button>

          <button
            data-testid="game-menu-exit"
            style={{ ...itemStyle, color: '#d9534f' }}
            onClick={() => { setOpen(false); onExit?.(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-el)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <X size={18} />
            <span>End game</span>
          </button>
        </div>
      )}

      {!open && (
        <button
          data-testid="game-menu-toggle"
          className="mute-btn"
          aria-label="Open game menu"
          onClick={() => setOpen(true)}
        >
          <MoreVertical size={26} />
        </button>
      )}
    </div>
  );
}
